/**
 * Homepage user-facing copy — single owner for strings (browse-first redesign).
 * Do not add homepage marketing/CTA strings in page.tsx or section components.
 */

export const HOMEPAGE_COPY = {
  hero: {
    prefix: "Fairly used",
    words: [
      "Bed & Mattress",
      "A.C.",
      "Smart TV",
      "Fridge",
      "Washing Machine",
      "Dining Set",
      "Microwave",
      "Sofa",
    ] as const,
    /** Static word when prefers-reduced-motion (full headline still readable). */
    staticWord: "Smart TV",
    suffix: "Closest to you at the Best Price",
    subline: "Buy and sell trusted household items in Lagos",
  },
  cta: {
    browsePrimary: "See available items",
    sellSecondary: "Sell item",
    nearMeSecondary: "Alert me for new items nearby",
    /** H14 — clear secondary when recent-requests feature on (not ghost tertiary). */
    requestSecondary: "Request an item",
  },
  feed: {
    title: "Near you",
    subtitleFallback: "in Lagos",
    subtitleWithNeighborhood: (neighborhood: string) => `in ${neighborhood}`,
  },
  shelves: {
    recentlySold: "Recently Sold",
    /** Benefit-first — not “Item Requests” jargon (H7 / Phase 3). */
    recentRequests: "What people are looking for",
    recentRequestsAudience: "Have one? Match a request.",
    recentRequestsEmptyTitle: "No public requests yet",
    recentRequestsEmptyBody:
      "Be the first to say what you need — sellers nearby can list to match.",
    recentRequestsEmptyCta: "Request an item",
    recentRequestsIHaveThis: "I have this",
    /** Expand when prefs diverge (count already in “N open”). */
    recentRequestsPickMatch: "Pick a match",
    /** Secondary expand when group already has I have this. */
    recentRequestsSeeOptions: "See options",
    recentRequestsCollapse: "Show less",
    recentRequestsShowMore: "Show more",
    recentRequestsShowLess: "Show less",
    recentRequestsOpenCount: (n: number) => `${n} open`,
  },
  education: {
    heading: "How it works",
    intro:
      "Safer than a random chat deal: listings are reviewed, you pay Sell It Off (not the seller), and you inspect before confirming.",
    sellersLabel: "Sellers",
    buyersLabel: "Buyers",
    sellerSteps: [
      {
        title: "List your item",
        description:
          "Add clear photos, details, and price. Listings stay private until our team has checked them.",
        icon: "list",
      },
      {
        title: "Get reviewed",
        description:
          "We review quality and authenticity—typically within 24 hours—before buyers can see your listing.",
        icon: "reviewed",
      },
      {
        title: "Meet the buyer",
        description:
          "Agree a nearby pickup time. The buyer inspects with you before anything is final.",
        icon: "meet",
      },
      {
        title: "Get paid by us",
        description:
          "After the buyer confirms pickup, Sell It Off releases your payment—you’re not chasing cash from strangers.",
        icon: "paid",
      },
    ] as const,
    buyerSteps: [
      {
        title: "Browse nearby",
        description:
          "Find fairly used household items close to you—only reviewed listings appear in the market.",
        icon: "browse",
      },
      {
        title: "Pay Sell It Off",
        description:
          "You pay the platform, not the seller. We hold the money until you confirm the item is good.",
        icon: "pay",
      },
      {
        title: "Inspect before you confirm",
        description:
          "See and test the item at pickup. Don’t confirm until you’re satisfied.",
        icon: "inspect",
      },
      {
        title: "Confirm to release payment",
        description:
          "Once you confirm pickup, we pay the seller. Confirm only after you’ve checked the item.",
        icon: "confirm",
      },
    ] as const,
  },
  faq: {
    heading: "Frequently Asked Questions",
    viewAllLabel: "View all FAQs",
    viewAllHref: "/faq",
    /**
     * Q&A resolved from `src/lib/faq` surface preset `homepage`
     * (protect-buyers-sellers → how-payments-work → item-not-as-described).
     */
  },
  testimonials: {
    heading: "What Our Users Say",
    items: [
      {
        text: "Sell It Off helped me sell all the items I no longer needed after relocating. The process was hassle-free, and the service was top-notch. I've referred them to several friends!",
        author: "Kingsley Uwah, Surveyor",
        location: "Awoyaya, Lagos Nigeria",
        image:
          "https://xqiegyvjcedzogkaznts.supabase.co/storage/v1/object/public/assets//kings.webp",
      },
      {
        text: "This is my go-to platform for great deals on used items. The product came just as described on the website, as good as new. Thanks for being awesome with the customer experience!",
        author: "Florence Akanbi, Product Designer",
        location: "Palmgrove, Lagos Nigeria",
        image:
          "https://xqiegyvjcedzogkaznts.supabase.co/storage/v1/object/public/assets//flo.webp",
      },
      {
        text: "One of the smoothest declutter platforms I've used. I will definitely continue to make use of them. Highly recommended!",
        author: "Ose Omokhiboria, Entrepreneur",
        location: "Sangotedo, Lagos Nigeria",
        image:
          "https://xqiegyvjcedzogkaznts.supabase.co/storage/v1/object/public/assets//ose.webp",
      },
    ] as const,
  },
  errors: {
    loadFailed: "Failed to load content. Please try refreshing the page.",
    retry: "Retry",
  },
} as const;

export type HomepageFeedLocationContext = {
  areaName?: string | null;
};

export function getHomepageFeedHeadline(
  locationContext?: HomepageFeedLocationContext | null,
): { title: string; subtitle: string } {
  const neighborhood = locationContext?.areaName?.trim();
  return {
    title: HOMEPAGE_COPY.feed.title,
    subtitle: neighborhood
      ? HOMEPAGE_COPY.feed.subtitleWithNeighborhood(neighborhood)
      : HOMEPAGE_COPY.feed.subtitleFallback,
  };
}

export function getHomepageStaticHeroHeadline(): string {
  const { prefix, staticWord, suffix } = HOMEPAGE_COPY.hero;
  return `${prefix} ${staticWord} ${suffix}`;
}
